import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import { StatusBadge } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Search, CheckCircle2, XCircle, Ban, RotateCcw, Download, Eye, Loader2, Trash2,
} from "lucide-react";

export default function Students() {
  const [students, setStudents] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/students", { params: { status, search } });
      setStudents(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [status, search]);

  const action = async (id, act, label) => {
    await api.post(`/admin/students/${id}/${act}`);
    toast.success(label);
    load();
  };

  const removeStudent = async () => {
    await api.delete(`/admin/students/${removeTarget.id}`);
    toast.success("Student removed — they can register again");
    setRemoveTarget(null);
    load();
  };

  const viewDetail = async (id) => {
    const { data } = await api.get(`/admin/students/${id}`);
    setDetail(data);
  };

  const downloadReport = async (id, name) => {
    const res = await api.get(`/admin/students/${id}/report`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}-report.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout nav={adminNav} title="Students">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Student Management</h1>
        <p className="text-muted-foreground mt-1">Approve enrollment requests and manage enrolled students.</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search name, email, reg. number…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="student-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="student-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "pending", "approved", "rejected", "suspended"].map((s) => (
              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Reg. No</TableHead>
              <TableHead>Roll</TableHead>
              <TableHead>Institution</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" /></TableCell></TableRow>
            )}
            {!loading && students.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No students found</TableCell></TableRow>
            )}
            {!loading && students.map((st) => {
              const enrolled = st.status === "approved" || st.status === "suspended";
              return (
                <TableRow key={st.id} data-testid={`student-row-${st.id}`}>
                  <TableCell>
                    <div className="font-medium">{st.name}</div>
                    <div className="text-xs text-muted-foreground">{st.email}</div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{st.registration_number}</TableCell>
                  <TableCell className="font-mono text-sm">{st.roll_number}</TableCell>
                  <TableCell className="text-sm">{st.institution}</TableCell>
                  <TableCell><StatusBadge status={st.status} /></TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {/* enrolled-only: view history + report */}
                      {enrolled && (
                        <>
                          <Button size="icon" variant="ghost" title="View history" onClick={() => viewDetail(st.id)} data-testid={`view-${st.id}`}><Eye className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" title="Download report" onClick={() => downloadReport(st.id, st.name)} data-testid={`download-${st.id}`}><Download className="h-4 w-4" /></Button>
                        </>
                      )}
                      {/* pending / rejected / info_requested: approve */}
                      {["pending", "rejected", "info_requested"].includes(st.status) && (
                        <Button size="icon" variant="ghost" title="Approve" className="text-emerald-600" onClick={() => action(st.id, "approve", "Student approved")} data-testid={`approve-${st.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                      )}
                      {/* pending: reject */}
                      {["pending", "info_requested"].includes(st.status) && (
                        <Button size="icon" variant="ghost" title="Reject" className="text-destructive" onClick={() => action(st.id, "reject", "Enrollment rejected")} data-testid={`reject-${st.id}`}><XCircle className="h-4 w-4" /></Button>
                      )}
                      {/* approved: suspend */}
                      {st.status === "approved" && (
                        <Button size="icon" variant="ghost" title="Suspend" className="text-destructive" onClick={() => action(st.id, "suspend", "Student suspended")} data-testid={`suspend-${st.id}`}><Ban className="h-4 w-4" /></Button>
                      )}
                      {/* suspended: reactivate */}
                      {st.status === "suspended" && (
                        <Button size="icon" variant="ghost" title="Reactivate" className="text-emerald-600" onClick={() => action(st.id, "reactivate", "Student reactivated")} data-testid={`reactivate-${st.id}`}><RotateCcw className="h-4 w-4" /></Button>
                      )}
                      {/* always: remove */}
                      <Button size="icon" variant="ghost" title="Remove student" className="text-destructive" onClick={() => setRemoveTarget(st)} data-testid={`remove-${st.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* remove confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the student account and their exam attempts. The email and
              registration number will be freed so they can register again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeStudent} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-remove">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* student detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-px bg-border border border-border text-sm">
                {[["Email", detail.email], ["Reg. No", detail.registration_number], ["Roll", detail.roll_number], ["Institution", detail.institution]].map(([k, v]) => (
                  <div key={k} className="bg-card p-3"><div className="overline">{k}</div><div className="mt-1">{v}</div></div>
                ))}
              </div>
              <div>
                <div className="overline mb-2">Examination History</div>
                {(detail.exam_history || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No published attempts yet.</div>
                ) : (
                  <div className="border border-border divide-y divide-border">
                    {detail.exam_history.map((a) => (
                      <div key={a.id} className="flex items-center justify-between p-3 text-sm">
                        <span>{a.exam_title}</span>
                        <span className="font-mono">{a.total_score}/{a.total_marks} · {a.percentage}% · {a.passed ? "PASS" : "FAIL"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
